import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ThemeProvider } from 'styled-components'
import { checkHasComplete, logInfoToDatadog } from '@/common/utils'
import type { AppDispatch, RootState } from '@/store/redux-store'
import { selectBotEvents } from '@/store/modules/bot-events'
import { selectProgress } from '@/store/modules/progress'
import {
  agentBotSet,
  backToIncomeVerification,
  closed,
  configUpdated,
  modelOpened,
  overlayOpened,
  overlayToggled,
  selectBotView,
  themeUpdated,
} from '@/store/modules/bot-view'
import { sendEvent } from '@/store/modules/webchat'
import { LoadingAnimation } from '../lottie/loading-animation'
import { OverallProgress } from '../progress'
import type { ThemeOptions as ProgressThemeOptions } from '../progress/types'
import { MobileProgressBar } from '../progress/mobile'
import { ModelType } from '../../types/model'
import { applyThemeCssVariables } from './apply-theme-css'
import Chat from './chat'
import { buildStyleOptions } from './style-options'
import {
  AgentBotGlobalStyle,
  ChatAndSidebar,
  ChatColumn,
  ChatPane,
  NothingToDo,
  OverlayToggle,
  PrimaryButton,
  SecondaryButton,
  Section,
  WebchatContainer,
  WebChatWrapper,
} from './styles'
import { useMediaQuery } from './use-media-query'

const SEND_BOX_CLASS = 'webchat__send-box-text-box__input'

export interface WebChatProps {
  onGoToSummary?: () => void
}

export const WebChat = ({ onGoToSummary }: WebChatProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const botEvents = useSelector((state: RootState) => selectBotEvents(state))
  const progress = useSelector((state: RootState) => selectProgress(state))
  const {
    model,
    showModal,
    showOverlay,
    hasOverlayToggle,
    themeOptions,
    sendBoxDisabled,
  } = useSelector((state: RootState) => selectBotView(state))

  const hasCompletedJourneyChecks = useRef(false)
  const journeyCompleted = useRef(false)
  const lastModelSeq = useRef(botEvents.model.seq)
  const lastOverlaySeq = useRef(botEvents.overlay.seq)
  const lastModalHiddenSeq = useRef(botEvents.modalHidden.seq)
  const [, setJourneyChecksVersion] = useState(0)

  const agentBot = Boolean(themeOptions.default.agentBot)
  const showDesktopProgress = isDesktop && !agentBot
  const showChatOverlay = showOverlay && !showModal
  const styleOptions = buildStyleOptions(themeOptions)
  const toggleCopy =
    typeof model.toggleCopy === 'string' && model.toggleCopy.length > 0
      ? model.toggleCopy
      : 'Upload'
  const backToIncomeVerificationEnabled = Boolean(
    model.enableBackToIncomeVerification,
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('src')) {
      dispatch(agentBotSet(true))
    }
  }, [dispatch])

  useEffect(() => {
    if (botEvents.model.seq <= lastModelSeq.current) return
    lastModelSeq.current = botEvents.model.seq
    dispatch(modelOpened(botEvents.model.payload as Record<string, unknown>))
  }, [botEvents.model, dispatch])

  useEffect(() => {
    if (botEvents.overlay.seq === 0) {
      lastOverlaySeq.current = 0
      return
    }
    if (botEvents.overlay.seq <= lastOverlaySeq.current) return
    lastOverlaySeq.current = botEvents.overlay.seq
    dispatch(overlayOpened(botEvents.overlay.payload as Record<string, unknown>))
  }, [botEvents.overlay, dispatch])

  useEffect(() => {
    if (botEvents.theme.seq === 0) return
    const payload = botEvents.theme.payload as Parameters<typeof themeUpdated>[0]
    dispatch(themeUpdated(payload))
    applyThemeCssVariables(payload)
  }, [botEvents.theme, dispatch])

  useEffect(() => {
    if (botEvents.config.seq === 0) return
    dispatch(configUpdated(botEvents.config.payload as Record<string, unknown>))
  }, [botEvents.config, dispatch])

  useEffect(() => {
    if (botEvents.modalHidden.seq <= lastModalHiddenSeq.current) return
    lastModalHiddenSeq.current = botEvents.modalHidden.seq
    dispatch(closed())
  }, [botEvents.modalHidden, dispatch])

  useEffect(() => {
    if (hasCompletedJourneyChecks.current) return
    if (
      progress.recommendations.length === 0 &&
      Object.keys(progress.flows).length === 0
    ) {
      return
    }

    hasCompletedJourneyChecks.current = true
    const completed = checkHasComplete(
      JSON.parse(JSON.stringify(progress)) as {
        flows: typeof progress.flows
        recommendations: typeof progress.recommendations
      },
    )
    journeyCompleted.current = completed
    if (completed) {
      logInfoToDatadog('User visited bot in completed state', {
        recommendations: progress.recommendations,
        flows: progress.flows,
      })
    }
    setJourneyChecksVersion(version => version + 1)
  }, [progress])

  useEffect(() => {
    const sendBox = document.getElementsByClassName(
      SEND_BOX_CLASS,
    )[0] as HTMLInputElement | undefined
    if (sendBox !== undefined) {
      sendBox.disabled = sendBoxDisabled
    }
  }, [sendBoxDisabled, showModal, showOverlay])

  const handleToggleOverlay = () => {
    dispatch(overlayToggled())
  }

  const handleBackToIncomeVerification = () => {
    dispatch(sendEvent({ name: 'back-to-income-verification' }))
    dispatch(backToIncomeVerification())
  }

  const handleGoToSummary = () => {
    onGoToSummary?.()
  }

  const overlayToggle = hasOverlayToggle ? (
    <OverlayToggle
      data-test-id="modal-prompt"
      $backToIncomeVerificationEnabled={backToIncomeVerificationEnabled}
    >
      <PrimaryButton type="button" onClick={handleToggleOverlay}>
        {toggleCopy}
      </PrimaryButton>
      {backToIncomeVerificationEnabled && (
        <SecondaryButton type="button" onClick={handleBackToIncomeVerification}>
          Back to income verification options
        </SecondaryButton>
      )}
    </OverlayToggle>
  ) : null

  const chat = journeyCompleted.current ? (
    <NothingToDo>
      <h3>
        There is currently nothing for you to do! If we need anything further from you, we
        will email you.
      </h3>
    </NothingToDo>
  ) : (
    <Chat
      styleOptions={styleOptions}
      showOverlay={showChatOverlay}
      model={model as unknown as ModelType}
    />
  )

  const webchat = (
    <Section>
      {!hasCompletedJourneyChecks.current && <LoadingAnimation key="loader" />}
      <ChatPane key="chat-pane">
        {!isDesktop && <MobileProgressBar onExit={handleGoToSummary} />}
        <ChatColumn key="chat" $hidden={!hasCompletedJourneyChecks.current}>
          {chat}
        </ChatColumn>
        {overlayToggle}
      </ChatPane>
    </Section>
  )

  return (
    <ThemeProvider theme={themeOptions}>
      <WebChatWrapper>
        {agentBot && <AgentBotGlobalStyle />}
        <WebchatContainer>
          <ChatAndSidebar $agentBot={!showDesktopProgress}>
            {webchat}
            {showDesktopProgress && (
              <OverallProgress
                themeOptions={themeOptions as ProgressThemeOptions}
                onBack={handleGoToSummary}
              />
            )}
          </ChatAndSidebar>
        </WebchatContainer>
      </WebChatWrapper>
    </ThemeProvider>
  )
}

export default WebChat
